/* eslint-disable @typescript-eslint/no-explicit-any */
import { MfaStatus } from '@qse/api'
import { type Drive, type User, UserRoleEnum, type UserStore } from '@qse/constants'
import { create } from 'zustand'

// Helper function to check if stored token is valid (legacy support)
const isStoredTokenValid = (): boolean => {
  try {
    // Check for session data first (new system)
    const sessionData = localStorage.getItem('session_data')

    if (sessionData) {
      const session = JSON.parse(sessionData)

      return session.isValid && Date.now() < session.expiresAt
    }

    // Fallback to legacy token check
    const tokenData = localStorage.getItem('token')

    if (!tokenData) return false

    const parsed = JSON.parse(tokenData)
    // Check if token is not older than 24 hours
    const isRecent = Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000

    return parsed.isValid && isRecent
  } catch (_error) {
    return false
  }
}

// Extended UserStore interface with quota management
interface ExtendedUserStore extends UserStore {
  // Quota management
  updateDriveQuota: (drive: Drive) => void
  getStorageInfo: () => {
    used: number
    allocated: number
    total: number
    utilizationPercent: number
    isUploading?: boolean
    isDeleting?: boolean
  } | null
  getApiQuotaInfo: () => {
    used: number
    total: number
    utilizationPercent: number
    isActive?: boolean
  } | null
  // Upload status management
  setUploadStatus: (isUploading: boolean) => void
  setDeletingStatus: (isDeleting: boolean) => void
  uploadStatus: {
    isUploading: boolean
    isDeleting: boolean
  }
  updateMfaStatus: (mfaStatus: MfaStatus) => void
  incrementApiQuota: (amount?: number) => void
}

// Helper function to preserve local quota increments when updating user from server
const preserveLocalQuotaIncrements = (
  newUser: User | null,
  currentUser: User | null,
): User | null => {
  if (!newUser || !currentUser?.drive?.quotaSummary) {
    return newUser
  }

  const currentDrive = currentUser.drive.quotaSummary
  const newDrive = newUser.drive?.quotaSummary

  if (!newDrive) {
    return newUser
  }

  let updatedUser = newUser

  // Preserve API quota increments
  if (currentDrive.service && newDrive.service) {
    const currentLocalQuota = currentDrive.service
    const serverQuota = newDrive.service

    // If the local quota usage is higher than server quota (indicating local increments),
    // preserve the local value to maintain consistency until server sync
    if (
      currentLocalQuota.used > serverQuota.used &&
      currentLocalQuota.quota === serverQuota.quota &&
      currentLocalQuota.used <= serverQuota.quota
    ) {
      updatedUser = {
        ...updatedUser,
        drive: {
          ...updatedUser.drive!,
          quotaSummary: {
            ...updatedUser.drive!.quotaSummary,
            service: {
              ...serverQuota,
              used: currentLocalQuota.used,
              utilizationPercent:
                serverQuota.quota > 0
                  ? Number(((currentLocalQuota.used / serverQuota.quota) * 100).toFixed(2))
                  : 0,
              remaining: Math.max(0, serverQuota.quota - currentLocalQuota.used),
            },
            storage: updatedUser.drive?.quotaSummary?.storage || newDrive.storage,
          },
        },
      }
    }
  }

  // Preserve storage quota changes (pending bytes, upload status, etc.)
  if (currentDrive.storage && newDrive.storage) {
    const currentStorage = currentDrive.storage
    const serverStorage = newDrive.storage

    // Preserve pending bytes and other local storage state
    if (currentStorage.pendingBytes !== serverStorage.pendingBytes) {
      updatedUser = {
        ...updatedUser,
        drive: {
          ...updatedUser.drive!,
          quotaSummary: {
            ...updatedUser.drive!.quotaSummary,
            storage: {
              ...serverStorage,
              pendingBytes: currentStorage.pendingBytes,
              // Recalculate utilization with pending bytes
              utilizationPercent:
                serverStorage.quota > 0
                  ? Number(
                      (
                        ((serverStorage.used + currentStorage.pendingBytes) / serverStorage.quota) *
                        100
                      ).toFixed(2),
                    )
                  : 0,
            },
            service: updatedUser.drive?.quotaSummary?.service || newDrive.service,
          },
        },
      }
    }
  }

  return updatedUser
}

export const useUserStore = create<ExtendedUserStore>(
  (
    set: (arg0: {
      user?: User | null
      isLoading?: boolean
      error?: string | null
      isAuthenticated?: boolean
      requiresBilling?: boolean
      uploadStatus?: {
        isUploading: boolean
        isDeleting: boolean
      }
    }) => any,
    get: () => ExtendedUserStore,
  ) => ({
    user: null,
    isLoading: false,
    error: null,
    isAuthenticated: isStoredTokenValid(),
    requiresBilling: false,
    uploadStatus: {
      isUploading: false,
      isDeleting: false,
    },

    setUser: (user: User | null) => {
      const state = get()

      // SUPER_ADMIN users never require billing regardless of isActive status
      const requiresBilling = user
        ? user.userRole === UserRoleEnum.SUPER_ADMIN ||
          user.userRole === UserRoleEnum.RESELLER_ADMIN
          ? false
          : user.tenant?.status === 'incomplete'
        : false

      // Preserve local quota increments when updating user from server
      const preservedUser = preserveLocalQuotaIncrements(user, state.user)

      set({ user: preservedUser, requiresBilling })
    },

    setLoading: (loading: boolean) => set({ isLoading: loading }),
    setError: (error: string | null) => set({ error }),
    setAuthenticated: (authenticated: boolean) => set({ isAuthenticated: authenticated }),
    setRequiresBilling: (requiresBilling: boolean) => {
      set({ requiresBilling })
    },

    // Method to set both user and authentication status together
    setUserAndAuth: (user: User | null, authenticated: boolean) => {
      const state = get()

      // Preserve local quota increments when updating user from server
      const preservedUser = preserveLocalQuotaIncrements(user, state.user)

      set({ user: preservedUser, isAuthenticated: authenticated })
    },

    // New method to check and update authentication status from session
    checkAuthenticationStatus: () => {
      const isAuthenticated = isStoredTokenValid()

      set({ isAuthenticated })

      return isAuthenticated
    },

    // Helper method to check if user needs billing
    checkBillingRequirement: () => {
      const state = get()

      // SUPER_ADMIN users never require billing
      if (
        state.user?.userRole === UserRoleEnum.SUPER_ADMIN ||
        state.user?.userRole === UserRoleEnum.RESELLER_ADMIN
      ) {
        if (state.requiresBilling !== false) {
          set({ requiresBilling: false })
        }

        return false
      }

      const requiresBilling = state.user ? state.user.tenant?.status === 'incomplete' : false

      if (state.requiresBilling !== requiresBilling) {
        set({ requiresBilling })
      }

      return requiresBilling
    },

    // New quota management methods
    updateDriveQuota: (drive: Drive) => {
      const state = get()

      if (state.user) {
        const updatedUser = {
          ...state.user,
          drive: drive,
        }

        set({ user: updatedUser })
      }
    },

    getStorageInfo: () => {
      const state = get()
      const drive = state.user?.drive

      if (!drive?.quotaSummary?.storage) {
        return null
      }

      const storage = drive.quotaSummary.storage
      const uploadStatus = state.uploadStatus

      return {
        used: storage.used / (1024 * 1024 * 1024), // Convert to GB
        allocated: (storage.used + storage.pendingBytes) / (1024 * 1024 * 1024), // Include pending uploads
        total: storage.quota / (1024 * 1024 * 1024), // Convert to GB
        utilizationPercent: storage.utilizationPercent,
        isUploading: uploadStatus.isUploading,
        isDeleting: uploadStatus.isDeleting,
      }
    },

    getApiQuotaInfo: () => {
      const state = get()
      const drive = state.user?.drive

      if (!drive?.quotaSummary?.service) {
        return null
      }

      const service = drive.quotaSummary.service

      return {
        used: service.used,
        total: service.quota,
        utilizationPercent: service.utilizationPercent,
        isActive: false, // This would be set based on active API calls
      }
    },

    setUploadStatus: (isUploading: boolean) => {
      const state = get()

      set({
        uploadStatus: {
          ...state.uploadStatus,
          isUploading,
        },
      })
    },

    setDeletingStatus: (isDeleting: boolean) => {
      const state = get()

      set({
        uploadStatus: {
          ...state.uploadStatus,
          isDeleting,
        },
      })
    },

    updateMfaStatus: (mfaStatus: MfaStatus) => {
      const state = get()

      if (state.user) {
        const updatedUser = {
          ...state.user,
          mfaStatus,
        }

        set({ user: updatedUser })
      }
    },

    // Method to increment API quota usage
    incrementApiQuota: (amount: number = 1) => {
      const state = get()

      if (state.user?.drive?.quotaSummary?.service) {
        const currentService = state.user.drive.quotaSummary.service
        const newUsed = currentService.used + amount
        const newUtilizationPercent =
          currentService.quota > 0 ? Number(((newUsed / currentService.quota) * 100).toFixed(2)) : 0
        const newRemaining = Math.max(0, currentService.quota - newUsed)

        const updatedUser = {
          ...state.user,
          drive: {
            ...state.user.drive,
            quotaSummary: {
              ...state.user.drive.quotaSummary,
              service: {
                ...currentService,
                used: newUsed,
                utilizationPercent: newUtilizationPercent,
                remaining: newRemaining,
              },
            },
          },
        }

        set({ user: updatedUser })
      }
    },

    reset: () =>
      set({
        user: null,
        isLoading: false,
        error: null,
        isAuthenticated: false,
        requiresBilling: false,
        uploadStatus: {
          isUploading: false,
          isDeleting: false,
        },
      }),
  }),
)
